// Scheduling Coverage Thresholds Storage
// Manages configurable thresholds that drive the resource scheduling health badges
// across the Overview widget, Scheduling tab, Dashboard alerts and Dashboard table.

export interface SchedulingThresholds {
  /** Minimum coverage % to be considered "Fully Resourced". Default: 95 */
  fullyResourcedMin: number;
  /** Minimum coverage % to be considered "Partially Resourced". Default: 50 */
  partiallyResourcedMin: number;
  /** Last updated ISO timestamp (set automatically on save) */
  lastUpdated?: string;
}

const STORAGE_KEY = 'scheduling-thresholds';

export const DEFAULT_SCHEDULING_THRESHOLDS: SchedulingThresholds = {
  fullyResourcedMin: 95,
  partiallyResourcedMin: 50,
};

/**
 * Load scheduling thresholds from localStorage.
 * Returns defaults if nothing has been saved yet.
 */
export function getSchedulingThresholds(): SchedulingThresholds {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SCHEDULING_THRESHOLDS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading scheduling thresholds:', error);
  }
  return { ...DEFAULT_SCHEDULING_THRESHOLDS };
}

/**
 * Save scheduling thresholds to localStorage.
 */
export function saveSchedulingThresholds(thresholds: SchedulingThresholds): void {
  try {
    const toSave: SchedulingThresholds = {
      ...thresholds,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving scheduling thresholds:', error);
    throw error;
  }
}

/**
 * Derive a health status string from a coverage percentage using the configured thresholds.
 */
export function deriveHealthStatus(
  coveragePercent: number,
  thresholds: SchedulingThresholds
): 'FullyResourced' | 'PartiallyResourced' | 'Unresourced' {
  if (coveragePercent >= thresholds.fullyResourcedMin) return 'FullyResourced';
  if (coveragePercent >= thresholds.partiallyResourcedMin) return 'PartiallyResourced';
  return 'Unresourced';
}
